// Small shared building blocks. Pages compose these; they don't restyle them.
import { Link, useLocation } from 'react-router-dom';
import { helpTopicFor } from '../../pages/help/articles/index.js';

/** Title row for every page: optional back link, title, subtitle, and actions on the right. */
export function PageHeader({ back, title, subtitle, actions, meta }) {
  const { pathname } = useLocation();
  const topic = helpTopicFor(pathname);
  const top = (back || topic) && (
    <div className="page-header-top">
      {back ? <Link to={back.to} className="back-link">← {back.label}</Link> : <span />}
      {topic && <HowThisPageWorks topic={topic} />}
    </div>
  );
  // A detail page (it has a way back): the title sits in a card, like events and messages
  if (back) {
    return (
      <div className="page-header">
        {top}
        <section className="card ev-hero detail-hero">
          {meta && <div className="ld-tags detail-meta">{meta}</div>}
          <div className="ev-hero-main">
            <div className="ev-hero-text">
              <h1>{title}</h1>
              {subtitle && <p className="ev-hero-state">{subtitle}</p>}
            </div>
            {actions && <div className="ev-hero-actions">{actions}</div>}
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className="page-header">
      {top}
      <div className="page-head">
        <div className="page-head-text">
          <h1>{title}</h1>
          {meta && <div className="page-meta">{meta}</div>}
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {actions && <div className="head-actions">{actions}</div>}
      </div>
    </div>
  );
}

/** The link to this page's help article (pages/help/articles). */
export function HowThisPageWorks({ topic }) {
  return (
    <Link to={`/help/${topic}`} className="page-help-link">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.7" /><path d="M12 17h.01" /></svg>
      How this page works
    </Link>
  );
}

/** A titled card. `actions` sit on the right of the title (e.g. an "Add" button). */
export function Section({ title, description, actions, children, flush = false, id, tone }) {
  const headingId = id ? `${id}-h` : undefined;
  return (
    <section className={`card section${flush ? ' section-flush' : ''}${tone ? ` section-${tone}` : ''}`} aria-labelledby={headingId}>
      {(title || actions) && (
        <div className="section-head">
          <div>
            {title && <h2 id={headingId}>{title}</h2>}
            {description && <p className="muted section-desc">{description}</p>}
          </div>
          {actions && <div className="section-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Nothing here yet: a sentence and (usually) the button that fixes it. */
export function EmptyState({ title, children, action }) {
  return (
    <div className="empty-state">
      {title && <h3>{title}</h3>}
      {children && <p className="muted">{children}</p>}
      {action}
    </div>
  );
}

/** Status / label pill. tone: good | warn | bad | info | navy | neutral */
export function Pill({ tone = 'neutral', children, title }) {
  return <span className={`pill pill-tone-${tone}`} title={title}>{children}</span>;
}

/** Label/value list for read-only details. items: [[label, value], …] */
export function Facts({ items }) {
  return (
    <dl className="facts">
      {items.filter(Boolean).map(([k, v]) => [<dt key={`${k}-t`}>{k}</dt>, <dd key={`${k}-d`}>{v ?? '—'}</dd>])}
    </dl>
  );
}

export function Loading({ children = 'Loading…' }) {
  return <p className="muted" role="status">{children}</p>;
}
